import { motion } from 'motion/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronUp, DollarSign, TrendingUp, AlertTriangle, Shield } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { getNonPhysicianRoleConfig, type ProgramRoleConfig, type NonPhysicianRole } from '@/lib/equip-program-config';
import { useEquipMeasureScheduleYear } from '@/hooks/equip/useEquipMeasureScheduleYear';
import {
  getRoleQuarterOutcomeMeasures,
  roleToBonusEligibleType,
  type ScheduleRole,
} from '@/lib/measureScheduleSelectors';
import type { MeasureScheduleItem } from '@/lib/equip-types';

interface ScenarioConfig {
  id: string;
  title: string;
  role: string;
  quarter: string;
  outcomesDescription: string;
  consistencyDescription: string;
  performance: number;
  award: number;
  maxAward: number;
  teachingPoint: string;
  icon: React.ReactNode;
  accent: 'success' | 'warning' | 'info' | 'danger';
}

interface ScenarioPresetsProps {
  track: 'physician' | 'non-physician';
  lockedRole?: NonPhysicianRole;
  employmentStatus?: import('@/data/types').EmploymentStatus;
  className?: string;
}

function buildRoleScenarios(
  config: ProgramRoleConfig,
  schedule: MeasureScheduleItem[],
): ScenarioConfig[] {
  const role = config.id as ScheduleRole;

  // Derive outcome and consistency counts from the canonical schedule
  const q4OutcomeMeasures = getRoleQuarterOutcomeMeasures(schedule, role, 'Q4');
  const q4Count = q4OutcomeMeasures.length;
  const isSingleMeasure = q4Count === 1;

  const bet = roleToBonusEligibleType(role);
  // Count consistency measures for Q4 (representative — same set across quarters)
  const consistencyCountUnique = schedule.filter(
    r =>
      r.measureCategory === 'Consistency' &&
      r.bonusEligibleType === bet &&
      r.isActive &&
      r.quarter === 4,
  ).length || 3; // fallback: standard 3 consistency measures

  // Scenario 1 — Perfect Quarter
  const scenario1: ScenarioConfig = {
    id: 'perfect-quarter',
    title: 'Perfect Quarter',
    role: `${config.name}, Full-Time`,
    quarter: 'Q4',
    outcomesDescription: `All ${q4Count} outcome measure${q4Count > 1 ? 's' : ''} met → 60%`,
    consistencyDescription: `All ${consistencyCountUnique} consistency measures met → 40%`,
    performance: 100,
    award: config.maxAward.fullTime,
    maxAward: config.maxAward.fullTime,
    teachingPoint: 'This is the ceiling. All measures met = full award.',
    icon: <TrendingUp className="w-5 h-5" />,
    accent: 'success',
  };

  // Scenario 2 — Outcome Measure Missed
  let scenario2: ScenarioConfig;
  if (isSingleMeasure) {
    const consistencyAward = Math.round(config.maxAward.fullTime * 0.4);
    const missedMeasureName = q4OutcomeMeasures[0] ?? 'Outcome measure';
    scenario2 = {
      id: 'outcome-missed',
      title: 'Outcome Measure Not Met',
      role: `${config.name}, Full-Time`,
      quarter: 'Any Quarter',
      outcomesDescription: `${missedMeasureName} not met → 0%`,
      consistencyDescription: `All ${consistencyCountUnique} consistency measures met → 40%`,
      performance: 40,
      award: consistencyAward,
      maxAward: config.maxAward.fullTime,
      teachingPoint: `The consistency floor protects $${consistencyAward.toLocaleString()} even when the outcome measure is not met. The 40% is yours if you meet the consistency standards.`,
      icon: <AlertTriangle className="w-5 h-5" />,
      accent: 'danger',
    };
  } else {
    const metCount = q4Count - 1;
    const outcomePercent = Math.round(60 * metCount / q4Count);
    const totalPercent = outcomePercent + 40;
    const award = Math.round(config.maxAward.fullTime * totalPercent / 100);
    scenario2 = {
      id: 'one-outcome-missed',
      title: 'One Outcome Not Met (Q4)',
      role: `${config.name}, Full-Time, Q4`,
      quarter: 'Q4',
      outcomesDescription: `${metCount} of ${q4Count} met → ${outcomePercent}%`,
      consistencyDescription: `All ${consistencyCountUnique} consistency measures met → 40%`,
      performance: totalPercent,
      award,
      maxAward: config.maxAward.fullTime,
      teachingPoint: `Missing one of ${q4Count} measures has a smaller proportional impact. Diversification across measures reduces the cost of any single shortfall.`,
      icon: <AlertTriangle className="w-5 h-5" />,
      accent: 'warning',
    };
  }

  // Scenario 3 — Consistency Safety Net
  const safetyNetAward = Math.round(config.maxAward.fullTime * 0.4);
  const scenario3: ScenarioConfig = {
    id: 'consistency-safety-net',
    title: 'Consistency Safety Net',
    role: `${config.name}, Full-Time`,
    quarter: 'Any Quarter',
    outcomesDescription: `All outcome measures unmet → 0%`,
    consistencyDescription: `All ${consistencyCountUnique} consistency measures met → 40%`,
    performance: 40,
    award: safetyNetAward,
    maxAward: config.maxAward.fullTime,
    teachingPoint: 'The 40% consistency floor provides a safety net even when outcome measures are not met.',
    icon: <Shield className="w-5 h-5" />,
    accent: 'info',
  };

  return [scenario1, scenario2, scenario3];
}

const physicianScenarios: ScenarioConfig[] = [
  {
    id: 'perfect-quarter',
    title: 'Perfect Quarter',
    role: 'Physician, Full-Time, Q4',
    quarter: 'Q4',
    outcomesDescription: 'All 6 outcome measures met → 60%',
    consistencyDescription: 'All 3 consistency measures met → 40%',
    performance: 100,
    award: 4000,
    maxAward: 4000,
    teachingPoint: 'This is the ceiling. All measures met = full award.',
    icon: <TrendingUp className="w-5 h-5" />,
    accent: 'success',
  },
  {
    id: 'q1-70-threshold',
    title: 'Q1 with 70% ACO Visits Threshold',
    role: 'Physician, Full-Time, Q1',
    quarter: 'Q1',
    outcomesDescription: 'ACO Visits at 75% (≥70%) ✓ → 60%',
    consistencyDescription: 'All 3 consistency measures met → 40%',
    performance: 100,
    award: 4000,
    maxAward: 4000,
    teachingPoint: 'Q1 has only ACO Visits as the outcome measure. The lower 70% threshold allows ramp-up time. 75% would not meet the threshold in Q2-Q4, but it meets the threshold in Q1.',
    icon: <DollarSign className="w-5 h-5" />,
    accent: 'info',
  },
  {
    id: 'mixed-results',
    title: 'Mixed Results',
    role: 'Physician, Full-Time, Q2',
    quarter: 'Q2',
    outcomesDescription: '4 of 5 outcome measures met → 48%',
    consistencyDescription: '2 of 3 consistency measures met → 26.67%',
    performance: 74.67,
    award: 2987,
    maxAward: 4000,
    teachingPoint: 'Meeting two measures still results in an incentive earning of nearly $3,000. There is opportunity to meet or exceed measures in the next quarter.',
    icon: <TrendingUp className="w-5 h-5" />,
    accent: 'warning',
  },
  {
    id: 'outcome-unmet-strong-consistency',
    title: 'Outcome Unmet, Strong Consistency',
    role: 'Physician, Full-Time, Q4',
    quarter: 'Q4',
    outcomesDescription: '4 of 6 outcome measures met → 40%',
    consistencyDescription: '3 of 3 consistency measures met → 40%',
    performance: 80,
    award: 3200,
    maxAward: 4000,
    teachingPoint: 'Not meeting 2 measures will result in an incentive earning of $3,200. The diversification across 6 measures allows for met measures to still receive recognition.',
    icon: <Shield className="w-5 h-5" />,
    accent: 'info',
  },
];

const apcScenarios: ScenarioConfig[] = [
  {
    id: 'perfect-quarter',
    title: 'Perfect Quarter',
    role: 'Primary Care APC, Full-Time, Q4',
    quarter: 'Q4',
    outcomesDescription: 'All 5 outcome measures met → 60%',
    consistencyDescription: 'All 3 consistency measures met → 40%',
    performance: 100,
    award: 3000,
    maxAward: 3000,
    teachingPoint: 'This is the ceiling. All measures met = full award.',
    icon: <TrendingUp className="w-5 h-5" />,
    accent: 'success',
  },
  {
    id: 'mixed-results',
    title: 'Mixed Results',
    role: 'Primary Care APC, Full-Time, Q2',
    quarter: 'Q2',
    outcomesDescription: '3 of 4 outcome measures met → 45%',
    consistencyDescription: '2 of 3 consistency measures met → 26.67%',
    performance: 71.67,
    award: 2150,
    maxAward: 3000,
    teachingPoint: 'Missing two measures still leaves over $2,000. In 2025, this might have been $0. That\'s the difference.',
    icon: <TrendingUp className="w-5 h-5" />,
    accent: 'warning',
  },
  {
    id: 'outcome-unmet-strong-consistency',
    title: 'Outcome Unmet, Strong Consistency (Q1)',
    role: 'Primary Care APC, Full-Time, Q1',
    quarter: 'Q1',
    outcomesDescription: '0 of 1 outcome measures met → 0%',
    consistencyDescription: '3 of 3 consistency measures met → 40%',
    performance: 40,
    award: 1200,
    maxAward: 3000,
    teachingPoint: 'The floor held. Consistency protected $1,200 even though the outcome measure missed. This is the safety net.',
    icon: <Shield className="w-5 h-5" />,
    accent: 'info',
  },
  {
    id: 'single-measure-role',
    title: 'Single-Measure Role',
    role: 'Psychiatry APC, Full-Time, Any Quarter',
    quarter: 'All',
    outcomesDescription: 'PHQ-9 at 88% (unmet) → 0%',
    consistencyDescription: '3 of 3 consistency measures met → 40%',
    performance: 40,
    award: 1200,
    maxAward: 3000,
    teachingPoint: 'Single-measure roles have higher stakes on outcomes. Not meeting PHQ-9 costs the full 60%. But consistency still provides $1,200. This is why we emphasize consistency for these roles.',
    icon: <AlertTriangle className="w-5 h-5" />,
    accent: 'danger',
  },
];

/** Scale scenarios to a representative part-time amount (3 days/week). */
function toPartTimeScenarios(scenarios: ScenarioConfig[], ftMax: number, ptMax: number): ScenarioConfig[] {
  const ratio = ptMax / ftMax;
  return scenarios.map((s) => ({
    ...s,
    role: s.role.replace(/Full-Time/g, 'Part-Time (3 days)'),
    award: Math.round(s.award * ratio),
    maxAward: ptMax,
  }));
}

export function ScenarioPresets({ track, lockedRole, employmentStatus, className }: ScenarioPresetsProps) {
  const { items: scheduleItems, isLoading, isError } = useEquipMeasureScheduleYear(new Date().getFullYear());

  let scenarios: ScenarioConfig[];
  if (lockedRole && track === 'non-physician' && !isLoading && !isError) {
    const config = getNonPhysicianRoleConfig(lockedRole);
    scenarios = config ? buildRoleScenarios(config, scheduleItems) : apcScenarios;
  } else {
    // Use static fallback while loading, on error, or for physician/generic tracks
    scenarios = track === 'physician' ? physicianScenarios : apcScenarios;
  }

  // Adjust scenarios for part-time view
  if (employmentStatus === 'part-time') {
    const ftMax = scenarios[0]?.maxAward ?? 4000;
    // Representative 3-day PT max: physician $800*3=$2,400, APC $600*3=$1,800
    const ptMax = track === 'physician' ? 2400 : 1800;
    scenarios = toPartTimeScenarios(scenarios, ftMax, ptMax);
  }

  return (
    <div className={cn('space-y-4', className)}>
      <h3 className="text-xl font-bold text-eventus-navy">
        Scenarios to walk through with your team
      </h3>
      <div className={cn('grid gap-4', scenarios.length % 2 === 0 ? 'md:grid-cols-2' : 'grid-cols-1')}>
        {scenarios.map((scenario, index) => (
          <ScenarioCard key={scenario.id} scenario={scenario} index={index} />
        ))}
      </div>
    </div>
  );
}

interface ScenarioCardProps {
  scenario: ScenarioConfig;
  index: number;
}

function ScenarioCard({ scenario, index }: ScenarioCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const accentColors = {
    success: 'border-green-200 bg-green-50',
    warning: 'border-amber-200 bg-amber-50',
    info: 'border-blue-200 bg-blue-50',
    danger: 'border-red-200 bg-red-50',
  };

  const iconColors = {
    success: 'text-green-600 bg-green-100',
    warning: 'text-amber-600 bg-amber-100',
    info: 'text-blue-600 bg-blue-100',
    danger: 'text-red-600 bg-red-100',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
    >
      <Card
        className={cn(
          'cursor-pointer transition-all hover:shadow-md',
          isExpanded && accentColors[scenario.accent]
        )}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className={cn('p-2 rounded-lg', iconColors[scenario.accent])}>
                {scenario.icon}
              </div>
              <div>
                <CardTitle className="text-lg text-eventus-navy">
                  {scenario.title}
                </CardTitle>
                <p className="text-base text-gray-500 mt-1">{scenario.role}</p>
              </div>
            </div>
            <div className="text-right">
              <Badge variant="outline" className="mb-2">
                {scenario.performance}%
              </Badge>
              <div className="text-2xl font-bold text-eventus-navy">
                ${scenario.award.toLocaleString()}
              </div>
              <div className="text-xs text-gray-500">
                of ${scenario.maxAward.toLocaleString()}
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <div className="space-y-2 text-base">
            <p className="text-gray-700">{scenario.outcomesDescription}</p>
            <p className="text-gray-700">{scenario.consistencyDescription}</p>
          </div>

          {/* Expandable Teaching Point */}
          <motion.div
            initial={false}
            animate={{ height: isExpanded ? 'auto' : 0, opacity: isExpanded ? 1 : 0 }}
            className="overflow-hidden"
          >
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-base font-medium text-gray-700 mb-1">Teaching point:</p>
              <p className="text-base text-gray-600 italic">"{scenario.teachingPoint}"</p>
            </div>
          </motion.div>

          <button
            className="mt-3 flex items-center gap-1 text-base text-eventus-purple hover:text-eventus-purple-dark transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-4 h-4" />
                Less detail
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                Teaching point
              </>
            )}
          </button>
        </CardContent>
      </Card>
    </motion.div>
  );
}
